import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../services/api";

const money = (value, currency = "NGN") =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(
    Number(value || 0)
  );

const statusClass = (status) => {
  if (status === "active") return "bg-emerald-50 text-emerald-700";
  if (["rejected", "closed"].includes(status)) return "bg-rose-50 text-rose-700";
  return "bg-amber-50 text-amber-700";
};

const FirmInvestmentDetail = () => {
  const { investmentId } = useParams();
  const [investment, setInvestment] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [available, setAvailable] = useState(null);
  const [settlementReference, setSettlementReference] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, allocationData] = await Promise.all([
        api.firmGetInvestment(investmentId),
        api.firmListAllocations(investmentId),
      ]);
      setInvestment(data);
      setAllocations(allocationData.items || []);
      setAvailable(allocationData.available || null);
    } catch (err) {
      setError(err.message || "Failed to load the asset deal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [investmentId]);

  const handleAction = async (action) => {
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      if (action === "submit") await api.firmSubmitInvestment(investmentId);
      if (action === "approve") {
        const note = window.prompt("Approval note (optional)") || "";
        await api.firmApproveInvestment(investmentId, { note });
      }
      if (action === "reject") {
        const note = window.prompt("Rejection reason");
        if (!note) throw new Error("A rejection reason is required.");
        await api.firmRejectInvestment(investmentId, { note });
      }
      if (action === "settle") {
        if (!settlementReference.trim()) throw new Error("Enter the bank, broker or custodian settlement reference.");
        await api.firmSettleInvestment(investmentId, { confirmation_reference: settlementReference.trim() });
        setSettlementReference("");
      }
      if (action === "close") await api.firmCloseInvestment(investmentId);
      setSuccess(
        action === "settle"
          ? "Settlement confirmed and balanced accounting entries posted."
          : `Deal ${action} action completed.`
      );
      await refresh();
    } catch (err) {
      setError(err.message || "Failed to update the asset deal.");
    } finally {
      setWorking(false);
    }
  };

  const accountingPreview = useMemo(() => {
    if (!investment) return [];
    const capitalised = (investment.costs || [])
      .filter((row) => row.accounting_treatment === "capitalise")
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const rows = [
      {
        account: "Investments under management",
        debit: Number(investment.gross_consideration || investment.principal || 0) + capitalised,
        credit: 0,
      },
    ];
    (investment.costs || [])
      .filter((row) => row.accounting_treatment !== "capitalise")
      .forEach((row) => rows.push({ account: row.description || row.cost_type, debit: Number(row.amount || 0), credit: 0 }));
    rows.push({ account: "Custodian cash", debit: 0, credit: Number(investment.settlement_amount || 0) });
    return rows;
  }, [investment]);

  if (loading) return <div className="text-sm text-slate-500">Loading asset deal…</div>;
  if (!investment) return <div className="text-sm text-slate-500">Asset deal not found.</div>;

  const currency = investment.currency || "NGN";
  const canSubmit = ["draft", "rejected"].includes(investment.status);
  const canApprove = investment.status === "pending_approval";
  const canSettle = investment.status === "awaiting_settlement";
  const canClose = ["active", "matured"].includes(investment.status);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/firm/investments" className="text-sm text-emerald-700 hover:underline">← Asset register</Link>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900">{investment.name}</h1>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(investment.status)}`}>
              {String(investment.status).replaceAll("_", " ")}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {investment.reference || `AST-${investment.id}`} · {investment.issuer || "Issuer not recorded"} · {investment.security_identifier || "No security identifier"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canSubmit ? <button disabled={working} onClick={() => handleAction("submit")} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">Submit for approval</button> : null}
          {canApprove ? <button disabled={working} onClick={() => handleAction("approve")} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white">Approve terms</button> : null}
          {canApprove ? <button disabled={working} onClick={() => handleAction("reject")} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white">Reject</button> : null}
          {canClose ? <button disabled={working} onClick={() => handleAction("close")} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">Close asset</button> : null}
        </div>
      </div>

      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {success ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div> : null}

      <div className="grid gap-4 md:grid-cols-4">
        {[
          ["Gross consideration", money(investment.gross_consideration || investment.principal, currency)],
          ["Associated costs", money(investment.total_costs, currency)],
          ["Cash settlement", money(investment.settlement_amount, currency)],
          ["Maturity", investment.maturity_date || "Not applicable"],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
            <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      {canSettle ? (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="font-semibold text-amber-900">Approved—awaiting settlement confirmation</h2>
          <p className="mt-1 text-sm text-amber-800">
            Confirm only after matching the bank, broker or custodian evidence. This posts the asset, transaction costs and cash movement as one balanced batch.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <input
              value={settlementReference}
              onChange={(event) => setSettlementReference(event.target.value)}
              placeholder="Settlement confirmation reference"
              className="flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
            />
            <button disabled={working} onClick={() => handleAction("settle")} className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white">
              Confirm settlement and post
            </button>
          </div>
        </section>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900">Instrument and trade terms</h2>
          <dl className="mt-4 grid gap-x-5 gap-y-4 sm:grid-cols-2">
            {[
              ["Instrument", String(investment.asset_type || "").replaceAll("_", " ")],
              ["Counterparty", investment.counterparty || "—"],
              ["Trade date", investment.trade_date || "—"],
              ["Settlement date", investment.settlement_date || "—"],
              ["Principal", money(investment.principal, currency)],
              ["Face value", money(investment.face_value, currency)],
              ["Stated rate", `${Number(investment.rate || 0).toFixed(4)}%`],
              ["Effective yield", `${Number(investment.effective_yield || 0).toFixed(4)}%`],
              ["Day count", investment.day_count],
              ["Income WHT", `${Number(investment.wht_rate || 0).toFixed(2)}%`],
            ].map(([label, value]) => <div key={label}><dt className="text-xs uppercase text-slate-400">{label}</dt><dd className="mt-1 text-sm font-medium capitalize text-slate-800">{value}</dd></div>)}
          </dl>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-semibold text-slate-900">Portfolio allocation</h2>
          {available ? (
            <p className="mt-1 text-xs text-slate-500">
              Available: {Number(available.quantity_available ?? available.principal_available ?? 0).toLocaleString()}
            </p>
          ) : null}
          <div className="mt-4 space-y-3">
            {allocations.length === 0 ? <p className="text-sm text-rose-600">No portfolio allocation recorded.</p> : allocations.map((row) => (
              <div key={row.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex justify-between gap-3">
                  <div><p className="font-medium text-slate-900">{row.owner_name || "Legacy client allocation"}</p><p className="text-xs capitalize text-slate-500">{String(row.owner_type || "legacy").replaceAll("_", " ")} · {row.owner_reference || "No reference"}</p></div>
                  <p className="text-sm font-semibold">{investment.asset_type === "stock" ? Number(row.quantity).toLocaleString() : money(row.principal, currency)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4"><h2 className="font-semibold text-slate-900">Associated costs and settlement accounting</h2></div>
        <div className="grid gap-6 p-5 lg:grid-cols-2">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-400"><tr><th className="py-2">Cost</th><th>Basis</th><th>Treatment</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {(investment.costs || []).map((row) => <tr className="border-t" key={row.id}><td className="py-2 capitalize">{row.description || row.cost_type.replaceAll("_", " ")}</td><td>{row.calculation_basis === "percentage" ? `${row.rate}%` : "Fixed"}</td><td className="capitalize">{row.accounting_treatment}</td><td className="text-right">{money(row.amount, currency)}</td></tr>)}
                {(investment.costs || []).length === 0 ? <tr><td colSpan="4" className="py-5 text-center text-slate-500">No associated costs recorded.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-400"><tr><th className="py-2">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
              <tbody>{accountingPreview.map((row, index) => <tr className="border-t" key={`${row.account}-${index}`}><td className="py-2 capitalize">{row.account.replaceAll("_", " ")}</td><td className="text-right">{row.debit ? money(row.debit, currency) : "—"}</td><td className="text-right">{row.credit ? money(row.credit, currency) : "—"}</td></tr>)}</tbody>
            </table>
            <p className="mt-2 text-xs text-slate-500">Ledger batch: {investment.ledger_batch_id || "Not posted until settlement"}</p>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4"><h2 className="font-semibold text-slate-900">Expected cashflows</h2></div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Due</th><th>Type</th><th>Gross</th><th>Tax</th><th>Net</th><th>Status</th></tr></thead>
            <tbody>{(investment.schedule || []).map((row) => <tr className="border-t" key={row.id}><td className="px-4 py-3">{row.due_date}</td><td className="capitalize">{row.cashflow_type}</td><td>{money(row.interest_component || row.principal_component, currency)}</td><td>{money(row.tax_component, currency)}</td><td>{money(row.net_amount || row.amount, currency)}</td><td className="capitalize">{row.status}</td></tr>)}</tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FirmInvestmentDetail;
